/**
 * Reading the session at boot WITHOUT letting a stuck auth lock hang the whole application.
 *
 * ⚠️ WHY THIS EXISTS. `supabase.auth.getSession()` waits on a Web Lock with no bound of its
 * own (see supabaseAuthLock.ts). Two callers await it before the app renders anything:
 *
 *   src/contexts/AuthContext.tsx  fetchSession()      -> ProtectedRoute renders "Loading..."
 *   src/App.tsx                   checkUserStatus()   -> LoadingFallback renders "Loading..."
 *
 * Both clear their loading flag only in a `finally`, so if `getSession()` never settles the
 * flag is never cleared and the user sits on a spinner until they manually reload the tab.
 * Bounding acquisition in supabaseAuthLock.ts made the observed trigger stop firing, but it
 * does not save the app from a lock that is genuinely never released: measured 2026-08-16,
 * holding the lock from an init script leaves the app on "Loading..." past 60 seconds even
 * with the bounded lock in place. The guarantee has to live at the await, which is here.
 *
 * ⚠️ THE TRADE-OFF. On timeout we fall back to the session supabase already persisted in
 * localStorage. That session is the same object `getSession()` would have returned in the
 * overwhelmingly common case — the wedge happens WITH a valid session, not without one — so
 * the fallback keeps the user signed in rather than bouncing them to /login and losing their
 * place. What it cannot do is prove the session is still valid server-side. It does not need
 * to: authentication is enforced by RLS on every query, not by this read, so the worst case
 * is a query that 401s and the existing `auth-signed-out` handling taking over.
 */

import type { Session } from '@supabase/supabase-js';

/** How long to wait for `getSession()` at boot before falling back to persisted state. */
export const SESSION_BOOT_TIMEOUT_MS = 8_000;

/**
 * How long a boot gate may block the first render before it gives up and renders anyway.
 *
 * ⚠️ THIS IS THE ACTUAL GUARANTEE, and it is deliberately separate from the bound above.
 * Bounding `getSession()` alone is whack-a-mole: measured 2026-08-16 with the auth lock held,
 * the `getSession()` bound fired correctly and the app STILL sat on "Loading...", because the
 * awaits after it — `enforceAccountActive()` in AuthContext, the `users` row read in App.tsx —
 * are ordinary Supabase queries that hang on exactly the same lock. Each new await added to a
 * boot path would need remembering. A watchdog that clears the loading flag no matter which
 * await is stuck does not.
 *
 * Longer than SESSION_BOOT_TIMEOUT_MS so the graceful path wins whenever it can, and the
 * watchdog is only reached when something downstream of it is stuck too.
 *
 * ⚠️ THREE GATES USE THIS AND THEIR BUDGETS ADD UP. AuthContext, then
 * ProtectedRouteWithResetCheck, then MainLayout — each mounts only once the previous one has
 * given up, so the worst case to reach something actionable is roughly THREE times this
 * value, not one.
 *
 * A shared deadline was tried and removed, because it cannot work against that shape: a gate
 * that only mounts after the previous one expires always arrives to find the deadline passed,
 * and arms a fresh full budget. Measured 2026-08-16 with the lock held and a 12s value, the
 * retry screen appeared at 37s — 12 + 12 + 12 — with the sharing supposedly in place. The
 * per-gate number IS the control.
 *
 * ⚠️ CALIBRATE IT AGAINST PRODUCTION, NOT LOCALHOST. It was briefly 5s, chosen because a
 * healthy boot on the dev server renders in 2-4s. Measured against app.joulescrm.com from a
 * clean browser on 2026-08-17: login 9.0s, app shell 4.9s — so 5s would have fired the
 * give-up screen on a PERFECTLY NORMAL load for anyone on an ordinary connection, and worse
 * for anyone slower. 12s is roughly 2.5x the measured healthy shell, which leaves room for a
 * bad network without waiting so long that the user has already reloaded.
 */
export const SESSION_BOOT_WATCHDOG_MS = 12_000;

/** The one method this needs from `Storage`, so it can be tested without a DOM. */
export interface StorageLike {
  getItem(key: string): string | null;
}

export interface GetSessionBoundedOptions {
  getSession: () => Promise<{ data: { session: Session | null }; error?: unknown }>;
  storage: StorageLike | undefined;
  storageKey: string;
  timeoutMs?: number;
  /** Called when the bound was reached and the persisted session was used instead. */
  onTimeout?: (timeoutMs: number) => void;
}

export interface BoundedSessionResult {
  session: Session | null;
  /** True when `getSession()` did not answer in time and the fallback was used. */
  timedOut: boolean;
  /**
   * Whatever `getSession()` reported, passed through untouched. AuthContext retries once on
   * an error before giving up, and folding that into a plain null session would delete the
   * retry without saying so.
   */
  error: unknown;
}

function looksLikeSession(value: unknown): value is Session {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { access_token?: unknown; user?: { id?: unknown } | null };
  return typeof candidate.access_token === 'string' && typeof candidate.user?.id === 'string';
}

/**
 * Read the session supabase persisted, without going near the auth lock.
 *
 * The stored value is a bare `Session` — verified against the running app rather than assumed,
 * since a wrapped `{ currentSession }` shape has existed in other versions of the library.
 *
 * ⚠️ An expired access token is still returned. `refresh_token` sits beside it and supabase
 * refreshes on the next call, so treating expiry as "signed out" here would eject every user
 * whose token aged past its hour — routine, not exceptional.
 */
export function readPersistedSession(
  storage: StorageLike | undefined,
  storageKey: string
): Session | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return looksLikeSession(parsed) ? parsed : null;
  } catch {
    // Unparseable JSON, or a storage read that throws because cookies are blocked. Either
    // way there is no session to be had, and throwing here would recreate the hang.
    return null;
  }
}

/**
 * `getSession()`, bounded. Resolves with the real session if it arrives in time, otherwise
 * with whatever supabase persisted, but always resolves.
 *
 * A real answer of "no session" is authoritative and is NOT overridden by persisted state —
 * otherwise signing out could never take effect while a stale value remained in storage.
 */
export async function getSessionBounded(
  options: GetSessionBoundedOptions
): Promise<BoundedSessionResult> {
  const { getSession, storage, storageKey, onTimeout } = options;
  const timeoutMs = options.timeoutMs ?? SESSION_BOOT_TIMEOUT_MS;

  let timer: ReturnType<typeof setTimeout> | undefined;
  const TIMED_OUT = Symbol('timed-out');

  try {
    const race = await Promise.race([
      getSession().then((result) => ({
        session: result?.data?.session ?? null,
        error: result?.error ?? null,
      })),
      new Promise<typeof TIMED_OUT>((resolve) => {
        timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs);
      }),
    ]);

    if (race === TIMED_OUT) {
      onTimeout?.(timeoutMs);
      return { session: readPersistedSession(storage, storageKey), timedOut: true, error: null };
    }

    return { session: race.session, timedOut: false, error: race.error };
  } catch (error) {
    // getSession() rejected outright. The persisted session is still the best answer
    // available, and propagating would leave the caller's loading flag set.
    return { session: readPersistedSession(storage, storageKey), timedOut: false, error };
  } finally {
    // ⚠️ Without this the timer keeps the event loop busy for the full bound after a fast
    // answer, which under fake timers is visible as a leaked pending timer.
    if (timer !== undefined) clearTimeout(timer);
  }
}
